import { useState, useEffect } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import { useParams } from "react-router-dom";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { getSelectStyles } from "../../../utils/selectStyles";
import "../../accounts/create/CreateAccountPage.css";

export default function EditReferralPage() {
  const { id } = useParams();

  const [accounts, setAccounts]     = useState([]);
  const [referer, setReferer]       = useState(null);
  const [refered, setRefered]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [updated, setUpdated]       = useState(false);
  const [error, setError]           = useState(null);

  const [referral, setReferral] = useState({ referer: "", refered: "", referDate: null, notes: "" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [refRes, accRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/referral/${id}`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
        ]);
        const data = { ...refRes.data, referDate: new Date(Number(refRes.data.referDate)).getTime() };
        setReferral(data);
        setAccounts(accRes.data);
        const toOpt = (acc) => acc ? ({ value: acc._id, label: `${acc.username}${acc.email ? ` (${acc.email})` : ''}` }) : null;
        setReferer(toOpt(accRes.data.find(a => a._id === data.referer)));
        setRefered(toOpt(accRes.data.find(a => a._id === data.refered)));
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load referral");
      }
    };
    fetchData();
  }, [id]);

  const loadOptions = (inputValue, callback) => {
    const filtered = accounts.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'referDate') {
      setReferral(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setReferral(prev => ({ ...prev, [name]: value }));
    }
  };

  const formatDateForInput = (unixMs) => unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setUpdated(false);
    setSubmitting(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/referral/${id}`,
        { referer: referral.referer, refered: referral.refered, notes: referral.notes, referDate: referral.referDate + "" },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setUpdated(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update referral");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Edit Referral" />
      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div>
              <div className="form-panel-title">Edit Referral</div>
              <div className="form-panel-subtitle">ID: {referral._id}</div>
            </div>
          </div>

          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              <div className="form-section-label">Accounts</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-fill" />Referer</label>
                    <AsyncSelect cacheOptions defaultOptions loadOptions={loadOptions}
                      onChange={opt => { if (!opt) return; setReferer(opt); setReferral(prev => ({ ...prev, referer: opt.value })); }}
                      value={referer} placeholder="Search account…" isClearable styles={getSelectStyles()} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-check-fill" />Referred</label>
                    <AsyncSelect cacheOptions defaultOptions loadOptions={loadOptions}
                      onChange={opt => { if (!opt) return; setRefered(opt); setReferral(prev => ({ ...prev, refered: opt.value })); }}
                      value={refered} placeholder="Search account…" isClearable styles={getSelectStyles()} />
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Details</div>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-event" />Referral Date</label>
                    <input className="form-input" type="date" name="referDate" value={formatDateForInput(referral.referDate)} onChange={handleChange} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-journal-text" />Notes</label>
                    <textarea className="form-input" name="notes" value={referral.notes || ''} onChange={handleChange}
                      rows={3} placeholder="Additional notes…" style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {updated && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Referral updated successfully!</div>}
              {error   && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button type="submit" className="btn-form-submit" disabled={submitting}>
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-check-lg'}`} />
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
